import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Icons } from '@/components/icons'
import {
    Button,
    Image,
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem
} from '@/components/nextui'
import { useAuthStore } from '@/store/authStore'

interface HeaderStepsProps {
    progress: string
    informationText: string
    logoutText: string
    supportText: string
    fullName?: string
    logout: () => void
    support: () => void
    setLoadingInscription: (loading: boolean) => void
}

// Extracted components to reduce the size of the main component
const UserGreeting = ({ fullName }: { fullName: string }) => {
    const { t } = useTranslation()

    return (
        <div className="px-4 py-1.5 text-medium font-medium text-center text-primary rounded-full">
            {t('headerSteps.greeting', { name: fullName })}
        </div>
    )
}

const AgentGreeting = ({
    currentAspirantName
}: {
    currentAspirantName: string
}) => {
    const { t } = useTranslation()

    return (
        <div className="px-8 py-3 text-medium font-medium text-center text-white rounded-full bg-gradient-to-r from-[#005BB5] to-[#00BF68] shadow-md">
            {currentAspirantName
                ? t('headerSteps.agentGreeting') +
                  ' ' +
                  t('headerSteps.agentHelping', { name: currentAspirantName })
                : t('headerSteps.agentGreeting') +
                  ' ' +
                  t('headerSteps.agentDefault')}
        </div>
    )
}

const NotificationBadge = ({
    logoutText,
    logout
}: {
    logoutText: string
    logout: () => void
}) => {
    return (
        <div className="flex items-center">
            <Button
                className="w-10 h-10 min-w-1"
                isIconOnly
                color="secondary"
                radius="full"
                onPress={logout}
            >
                <Icons.SignOutAlt />
            </Button>
        </div>
    )
}

const InscriptionSelector = ({
    setLoadingInscription
}: {
    setLoadingInscription: (loading: boolean) => void
}) => {
    const { inscriptions, idInscription, updateIdInscription } = useAuthStore()

    const handleInscriptionChange = async (key: string) => {
        setLoadingInscription(true)
        updateIdInscription(Number(key))
        // The loader will be turned off by the useEffect in page.tsx after updateForms completes
    }

    if (!inscriptions || inscriptions.length <= 1) {
        return null
    }

    const currentIndex = inscriptions.findIndex(
        (ins) => ins.id === idInscription
    )
    const displayIndex = currentIndex >= 0 ? currentIndex + 1 : 1

    return (
        <div className="pt-4 md:pt-0">
            <Dropdown placement="bottom-end">
                <DropdownTrigger>
                    <Button
                        variant="bordered"
                        color="primary"
                        className="mr-4 min-w-[9rem] justify-between"
                        endContent={<Icons.AngleDown />}
                    >
                        Inscripción #{displayIndex}
                    </Button>
                </DropdownTrigger>
                <DropdownMenu
                    aria-label="Seleccionar inscripción"
                    className="min-w-[14rem]"
                    itemClasses={{
                        base: [
                            'rounded-md gap-2 transition-colors bg-transparent',
                            'data-[hover=true]:bg-primary-50 data-[hover=true]:text-primary-900',
                            'data-[selectable=true]:focus:bg-primary-50'
                        ].join(' ')
                    }}
                    onAction={(key) => {
                        void handleInscriptionChange(String(key))
                    }}
                >
                    {inscriptions.map((ins, index) => {
                        const isSelected = ins.id === idInscription

                        return (
                            <DropdownItem
                                key={ins.id}
                                textValue={`Inscripción ${index + 1}`}
                                className={
                                    isSelected
                                        ? 'font-semibold text-primary'
                                        : ''
                                }
                                startContent={
                                    <span
                                        className={`flex w-4 shrink-0 items-center justify-center ${isSelected ? 'text-primary' : 'text-transparent'}`}
                                        aria-hidden={!isSelected}
                                    >
                                        {isSelected ? (
                                            <Icons.Check className="h-3.5 w-3.5" />
                                        ) : (
                                            <span className="inline-block h-3.5 w-3.5" />
                                        )}
                                    </span>
                                }
                            >
                                <span className="flex flex-col gap-0.5">
                                    <span>
                                        Inscripción #{index + 1}{' '}
                                        {ins.isActive ? (
                                            <span className="text-success-600 text-xs font-normal">
                                                (Activa)
                                            </span>
                                        ) : (
                                            <span className="text-default-500 text-xs font-normal">
                                                (Inactiva)
                                            </span>
                                        )}
                                    </span>
                                </span>
                            </DropdownItem>
                        )
                    })}
                </DropdownMenu>
            </Dropdown>
        </div>
    )
}

const ProgressIndicator = ({
    progress,
    isAgent,
    currentAspirantName
}: {
    progress: string
    isAgent: boolean
    currentAspirantName: string
}) => {
    const { t } = useTranslation()

    return (
        <div className="flex items-center gap-2 mt-2">
            <Icons.Route />
            <p className="text-base">
                {isAgent && currentAspirantName
                    ? t('headerSteps.progressAgent', {
                          name: currentAspirantName
                      })
                    : t('headerSteps.progressAspirant')}
            </p>
            <p className="text-xl font-bold text-primary">{progress}%</p>
        </div>
    )
}

export const HeaderSteps = ({
    progress,
    informationText,
    logoutText,
    supportText,
    fullName,
    logout,
    support,
    setLoadingInscription
}: HeaderStepsProps): JSX.Element => {
    const { isAgent, firstName: aspirantName } = useAuthStore()
    const [currentAspirantName, setCurrentAspirantName] = useState<string>(
        aspirantName || ''
    )

    // Listen for changes in the aspirant name in storage
    useEffect(() => {
        // Update current aspirant name when it changes in the store
        setCurrentAspirantName(aspirantName || '')

        // Create a storage event listener to detect changes across tabs/windows
        const handleStorageChange = () => {
            const storedName = localStorage.getItem('firstName')

            if (storedName) {
                try {
                    setCurrentAspirantName(JSON.parse(storedName))
                } catch (e) {
                    console.error('Error parsing stored aspirant name', e)
                }
            }
        }

        window.addEventListener('storage', handleStorageChange)

        return () => window.removeEventListener('storage', handleStorageChange)
    }, [aspirantName])

    return (
        <div
            className={`w-full ${isAgent ? 'bg-gradient-to-b from-blue-50 to-green-50 p-4 rounded-xl shadow-md border border-green-100' : ''}`}
        >
            {isAgent ? (
                // Agent layout with centered title
                <div className="flex flex-col items-center">
                    <div className="w-full flex justify-center mb-4">
                        <AgentGreeting
                            currentAspirantName={currentAspirantName}
                        />
                    </div>
                    <div className="w-full flex items-center justify-between">
                        <figure>
                            <Image
                                src="/LogoEAN.jpg"
                                alt="logo"
                                className="w-[130px]"
                            />
                        </figure>
                        <ProgressIndicator
                            progress={progress}
                            isAgent={isAgent}
                            currentAspirantName={currentAspirantName}
                        />
                    </div>
                </div>
            ) : (
                // Standard aspirant layout
                <div>
                    <div className="flex items-center justify-between w-full">
                        <figure>
                            <Image
                                src="/LogoEAN.jpg"
                                alt="logo"
                                className="w-[130px]"
                            />
                        </figure>

                        {/* Center section for user name */}
                        <div className="flex-grow flex justify-center items-center">
                            {fullName && <UserGreeting fullName={fullName} />}
                        </div>

                        {/* Right section: notification badge for aspirants only */}
                        <div className="flex items-center flex-col gap-2 md:flex-row">
                            <InscriptionSelector
                                setLoadingInscription={setLoadingInscription}
                            />
                            <NotificationBadge
                                logoutText={logoutText}
                                logout={logout}
                            />
                        </div>
                    </div>

                    <ProgressIndicator
                        progress={progress}
                        isAgent={isAgent}
                        currentAspirantName={currentAspirantName}
                    />
                </div>
            )}
        </div>
    )
}
